import { Box, styled } from '@chronosphereio/chrono-ui';
import { TimeRangeInputValue } from '@chronosphereio/core';
// This is a temporary override for an old use case until we complete the Chrono UI Popover backfill (sc-138310)
// eslint-disable-next-line no-restricted-imports
import { Popover } from '@mui/material';
import { forwardRef, useRef, useState } from 'react';
import { NamedTimeOption } from '@/core';
import { TimeZone } from '../context';
import { TimeRangeSelect } from './TimeRangeSelect/TimeRangeSelect';
import { TimeRangeTextFields } from './TimeRangeTextFields/TimeRangeTextFields';
import type { TimeOption } from './utils';

export interface DateTimeRangePickerProps extends TimeZone {
  /**
   * Callback to update the time range with the new values.
   */
  onChange: (newTimeRange: TimeRangeInputValue) => void;
  /**
   * The initial time range to render. This can be absolute or relative time.
   */
  timeRange: TimeRangeInputValue;
  /**
   * The relative time range options shown in the dropdown.
   */
  timeOptions?: NamedTimeOption | TimeOption[];
  /**
   * The maximum date that can be selected in the calendar.
   */
  maxDate?: Date;
  /**
   * The minimum date that can be selected in the calendar.
   */
  minDate?: Date;

  /**
   * When true, the date time range picker attempts to behave more responsively, so that it can be more flexible about
   * the space it takes up. This prop should be set by the `TimeRangeControls`, which contains sufficient context on
   * when it is safe to enable this functionality.
   */
  enableResponsiveWidth?: boolean;

  /**
   * When true, the time zone override is disabled.
   */
  disableTimeZoneOverride?: boolean;
}

/**
 * Control for selecting a date time range. In most use cases, consumers should use `TimeRangeControls` instead, which
 * includes the `DateTimeRangePicker` and other common functionality that should be kept consistent throughout our
 * applications.
 */
export const DateTimeRangePicker = forwardRef<HTMLDivElement, DateTimeRangePickerProps>(function DateTimeRangePicker(
  {
    timeRange,
    onChange,
    timeZone,
    userTimeZone,
    timeOptions,
    maxDate,
    minDate,
    setTimeZone,
    enableResponsiveWidth,
    disableTimeZoneOverride,
    ...props
  },
  ref
) {
  const [showCustomDateSelector, setShowCustomDateSelector] = useState(false);
  const anchorEl = useRef<HTMLDivElement | null>(null);

  return (
    <Box
      {...props}
      ref={ref}
      className="DateTimeRangePicker"
      sx={{ width: enableResponsiveWidth ? '100%' : undefined }}
    >
      <TimeRangeSelectWrapper ref={anchorEl} sx={{ justifyContent: enableResponsiveWidth ? 'flex-end' : undefined }}>
        <TimeRangeSelect
          value={timeRange}
          onCustomDateRangeClick={() => {
            setShowCustomDateSelector(true);
          }}
          onChange={onChange}
          timeOptions={timeOptions}
          timeZone={timeZone}
          setTimeZone={setTimeZone}
          userTimeZone={userTimeZone}
          minDate={minDate}
          maxDate={maxDate}
          enableResponsiveWidth={enableResponsiveWidth}
          disableTimeZoneOverride={disableTimeZoneOverride}
        />
      </TimeRangeSelectWrapper>
      <Popover
        anchorEl={anchorEl.current}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: enableResponsiveWidth ? 'right' : 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: enableResponsiveWidth ? 'right' : 'left',
        }}
        open={showCustomDateSelector}
        onClose={() => setShowCustomDateSelector(false)}
      >
        <TimeRangeTextFields
          initialTimeRange={timeRange}
          onTimeRangeChange={(value) => {
            onChange(value);
            setShowCustomDateSelector(false);
          }}
          onCancel={() => setShowCustomDateSelector(false)}
          timeZone={timeZone}
          maxDate={maxDate}
          minDate={minDate}
        />
      </Popover>
    </Box>
  );
});

const TimeRangeSelectWrapper = styled(Box)(() => {
  return {
    // Flex on the wrapper avoids the Autocomplete-based Select from greedily
    // filling all the available horizontal space.
    display: 'flex',
  };
});
